import React, { lazy, Suspense, useEffect } from "react";

import IdleChecker from "./components/IdleChecker";
import ProtectedRoute from './components/ProtectedRoute';
import RouteLayout from './components/RouteLayout';
import RouteLayoutOpen from './components/RouteLayoutOpen';
import SeoCanonical from './components/SeoCanonical';
import URLNormalizer from "./components/URLNormalizer";
import CanonicalHandler from "./components/CanonicalHandler";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// ── Route-level components — lazy loaded ────────────────────────────────────
const LoginPage = lazy(() => import("./components/authv2/LoginPage"));
const SignupPage = lazy(() => import("./components/authv2/SignupPage"));
const EmailVerifyAccount    = lazy(() => import("./components/signup/EmailVerifyAccount"));
const PhoneVerifyAccount    = lazy(() => import("./components/signup/PhoneVerifyAcconut"));
const RecoveryPage = lazy(() => import("./components/authv2/RecoveryPage"));
const Reset                 = lazy(() => import("./components/customer/PasswordReset"));
const UpdateWeb             = lazy(() => import("./components/update-web"));
const LandingPage           = lazy(() => import("./components/LandingV2"));
const TermsAndConditionsPage = lazy(() => import("./components/TermsAndConditionsPage"));
const DeleteAccountPage = lazy(() => import("./components/DeleteAccountPage"));
const PrivacyPolicyPage = lazy(() => import("./components/PrivacyPolicyPage"));
const FacilityLandingV2     = lazy(() => import("./components/FacilitiesV2"));
const PaymentComplete = lazy(() => import("./components/PaymentComplete"));
const FacilitySignup        = lazy(() => import("./components/FacilitySignup"));
const QuickFacilitySignup   = lazy(() => import("./components/QuickFacilitySignup"));
const FacilityResumeOnboarding = lazy(() => import("./components/FacilityResumeOnboarding"));
const FacilityAgreement     = lazy(() => import("./components/FacilityAgreement"));
const CheckResults          = lazy(() => import("./components/CheckResults"));
const Home                  = lazy(() => import("./components/customer/dashboard/Home"));
const CustomerOrders        = lazy(() => import("./components/customer/orders/CustomerOrders"));
const OrderDetails          = lazy(() => import("./components/customer/orders/OrderDetails"));
const OrderPDF              = lazy(() => import("./components/customer/orders/OrderPDF"));
const OrderResults          = lazy(() => import("./components/customer/orders/OrderResults"));
const CustomerFacilityDetail = lazy(() => import("./components/customer/FacilityPage"));
const CustomerTestDetail    = lazy(() => import("./components/customer/TestPage"));
const Account               = lazy(() => import("./components/customer/account/Account"));
const Cart                  = lazy(() => import("./components/customer/orders/CartPage"));
const CollectionPoint       = lazy(() => import("./components/customer/CollectionPoint"));
const OrderConfirmation     = lazy(() => import("./components/customer/orders/OrderConfirmPage"));
const TestView              = lazy(() => import("./components/customer/TestView"));
const DeliveryDashboard      = lazy(() => import("./components/customer/DeliveryDashboard"));

// ── Admin — facility partner onboarding review (ACU-501/502) ────────────────
const FacilityReviewList    = lazy(() => import("./components/admin/FacilityReviewList"));
const FacilityDetail        = lazy(() => import("./components/admin/FacilityDetail"));
const AdminLayout           = lazy(() => import("./components/admin/AdminLayout"));
const AgreementPage         = lazy(() => import("./components/facility/AgreementPage"));

const PageLoader = () => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"60vh", flexDirection:"column", gap:"12px" }}>
    <div style={{ width:"36px", height:"36px", border:"3px solid #e5e7eb", borderTop:"3px solid #2563eb", borderRadius:"50%", animation:"spin 0.8s linear infinite" }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const App = () => {
  const dispatch = useDispatch();

  // ACU-204.1: Removed beforeunload logout
  // redux-persist handles session persistence across page refreshes
  useEffect(() => {}, [dispatch]);

  return (
    <Router>
      <URLNormalizer />
      <CanonicalHandler />
      <div className="flex flex-col min-h-screen">
        <div className="flex-grow">
          <SeoCanonical />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/signup" exact element={<RouteLayoutOpen><SignupPage /></RouteLayoutOpen>} />
              <Route path="/email-verify-account" element={<RouteLayoutOpen><EmailVerifyAccount /></RouteLayoutOpen>} />
              <Route path="/phone-verify-account" element={<RouteLayoutOpen><PhoneVerifyAccount /></RouteLayoutOpen>} />
              <Route path="/login" exact element={<RouteLayoutOpen><LoginPage /></RouteLayoutOpen>} />
              <Route path="/phonenumber-login" exact element={<RouteLayoutOpen><LoginPage /></RouteLayoutOpen>} />
              <Route path="/password-recovery" element={<RouteLayoutOpen><RecoveryPage /></RouteLayoutOpen>} />
              <Route path="/password-reset" element={<Reset />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="/terms-and-conditions" element={<RouteLayoutOpen><TermsAndConditionsPage /></RouteLayoutOpen>} />
              <Route path="/delete-account" element={<RouteLayoutOpen><DeleteAccountPage /></RouteLayoutOpen>} />
              <Route path="/privacy-policy" element={<RouteLayoutOpen><PrivacyPolicyPage /></RouteLayoutOpen>} />
              <Route path="/facilities" element={<FacilityLandingV2 />} />
              <Route path="/facility-signup" element={<RouteLayoutOpen><QuickFacilitySignup /></RouteLayoutOpen>} />
              <Route path="/facility-onboarding-resume" element={<RouteLayoutOpen><FacilityResumeOnboarding /></RouteLayoutOpen>} />
              <Route path="/facility-agreement" element={<RouteLayoutOpen><FacilityAgreement /></RouteLayoutOpen>} />
              <Route path="/check-results" element={<RouteLayoutOpen><CheckResults /></RouteLayoutOpen>} />
              <Route path="/update-web/:id" exact element={<UpdateWeb />} />
              <Route path="/facility/agreement" element={<RouteLayoutOpen><AgreementPage /></RouteLayoutOpen>} />

              <Route path="/dashboard/:view" element={<ProtectedRoute><IdleChecker><RouteLayout><Home /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/my-orders" element={<ProtectedRoute><IdleChecker><RouteLayout><CustomerOrders /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/order-details/:orderId" element={<ProtectedRoute><IdleChecker><RouteLayout><OrderDetails /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/order-details/:orderId/results" element={<ProtectedRoute><IdleChecker><RouteLayout><OrderResults /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/order-details/:orderId/pdf" element={<ProtectedRoute><IdleChecker><RouteLayout><OrderPDF /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/facility/:id" element={<ProtectedRoute><IdleChecker><RouteLayout><CustomerFacilityDetail /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/tests/:id" element={<ProtectedRoute><IdleChecker><RouteLayout><CustomerTestDetail /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/facilities/test/:test" element={<ProtectedRoute><IdleChecker><RouteLayout><TestView /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/delivery-dashboard" element={<ProtectedRoute allowedRoles={["ADMIN", "FACILITY_ADMIN"]}><IdleChecker><RouteLayout><DeliveryDashboard /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/cart" element={<ProtectedRoute><IdleChecker><RouteLayout><Cart /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><IdleChecker><RouteLayout><Account /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/collection/order" element={<ProtectedRoute><IdleChecker><RouteLayout><CollectionPoint /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/order-confirm/:cart_id?" element={<ProtectedRoute><IdleChecker><RouteLayout><OrderConfirmation /></RouteLayout></IdleChecker></ProtectedRoute>} />
              <Route path="/payment-complete" element={<ProtectedRoute><IdleChecker><RouteLayout><PaymentComplete /></RouteLayout></IdleChecker></ProtectedRoute>} />

              {/* Admin — Facility Partner Onboarding Review (ACU-501/502) */}
              <Route
                path="/dashboard/admin"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "COMPLIANCE_REVIEWER"]}>
                    <AdminLayout><FacilityReviewList /></AdminLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/admin/facility/:id"
                element={
                  <ProtectedRoute allowedRoles={["ADMIN", "COMPLIANCE_REVIEWER"]}>
                    <AdminLayout><FacilityDetail /></AdminLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </div>
    </Router>
  );
};

export default App;
